import { useLingui } from "@lingui/react/macro";
import styles from "./ActivitiesList.module.scss";
import NoResults from "@/components/NoResults/NoResults";
import ActivityTable from "./ActivityTable";
import { endOfDay, isPast } from "date-fns";
import { dateName } from "@/Utils/DateUtils";
import { Heading } from "@medoma/components/Heading/Heading";
import { machineFormat } from "@medoma/models/date-and-time";
import type { IActivityOccurrenceOrGroupWithRequiredPatient } from "@medoma/models/activities";

type IActivitiesList = {
  activityOccurrencesAndGroups: IActivityOccurrenceOrGroupWithRequiredPatient[];
  displayedDays: Date[];
  isPlaceholderData: boolean;
  patientName: string;
};

const ActivitiesList = ({
  activityOccurrencesAndGroups,
  displayedDays,
  isPlaceholderData,
  patientName,
}: IActivitiesList) => {
  const { t } = useLingui();

  return (
    <ul
      aria-label={t`Lista med aktiviteter`}
      className={styles.activityOccurrencesTableList}
    >
      {displayedDays.map((day, index) => {
        const nameOfDay = dateName(day);
        const activityOccurrencesOnDay = activityOccurrencesAndGroups.filter(
          (activityOccurrencesAndGroups) =>
            machineFormat(activityOccurrencesAndGroups.start, "yyyy-MM-dd") ===
            machineFormat(day, "yyyy-MM-dd"),
        );

        // While fetching the previous day, we don't want to show the "no activities" message until we know.
        if (isPlaceholderData && index === 0) {
          return null;
        }

        // Only show "no activities" message for past days.
        if (activityOccurrencesOnDay.length === 0 && isPast(endOfDay(day))) {
          return (
            <li key={machineFormat(day, "yyyy-MM-dd")}>
              <NoResults
                message={t`${patientName} hade inga aktiviteter ${nameOfDay}`}
              />
            </li>
          );
        }

        // For later days with no activities, don't show anything.
        if (activityOccurrencesOnDay.length === 0) {
          return null;
        }

        return (
          <li
            key={machineFormat(day, "yyyy-MM-dd")}
            aria-labelledby={machineFormat(day, "yyyy-MM-dd")}
          >
            <Heading
              level="h2"
              id={machineFormat(day, "yyyy-MM-dd")}
              className={styles.dayHeading}
            >{`${dateName(day)} (${activityOccurrencesOnDay.length})`}</Heading>
            <ActivityTable
              activityOccurrencesAndGroups={activityOccurrencesAndGroups.filter(
                ({ start }) =>
                  machineFormat(start, "yyyy-MM-dd") ===
                  machineFormat(day, "yyyy-MM-dd"),
              )}
            />
          </li>
        );
      })}
    </ul>
  );
};

export { ActivitiesList };
